import { FormEvent, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  updateVacancy,
  Vacancy,
  VacancyUpdate,
} from '../../../../network/ats';
import { useToast } from '../../../../utils/useToast';
import {
  errorMessage,
  fieldClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../Pipeline/Modal';

type Props = {
  vacancy: Vacancy;
  onDone: () => void;
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN', 'CAD', 'AUD'];
const SALARY_TIMES = ['Per year', 'Per month', 'Per week', 'Per hour'];

/** Edits a vacancy's title, description, location, salary, status and visibility. */
const EditVacancy = ({ vacancy, onDone }: Props) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [values, setValues] = useState({
    jobTitle: vacancy.jobTitle ?? '',
    description: vacancy.description ?? '',
    jobLocation: vacancy.jobLocation ?? '',
    minimumSalary: vacancy.minimumSalary ?? '',
    maximumSalary: vacancy.maximumSalary ?? '',
    currency: vacancy.currency ?? 'USD',
    salaryTime: vacancy.salaryTime ?? 'Per year',
    status: vacancy.status === 'closed' ? 'closed' : 'open',
    visibility: vacancy.visibility === 'public' ? 'public' : 'private',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const set =
    (field: keyof typeof values) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) =>
      setValues((prev) => ({ ...prev, [field]: event.target.value }));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!values.jobTitle.trim()) {
      setError('Job title is required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateVacancy(vacancy.id, {
        ...values,
        jobTitle: values.jobTitle.trim(),
      } as VacancyUpdate);
      toast({ variant: 'success', title: 'Vacancy saved.' });
      queryClient.invalidateQueries(['vacancies']);
      queryClient.invalidateQueries(['ats-summary']);
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'The vacancy could not be saved.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="mx-auto flex flex-col gap-4 rounded-lg bg-white p-6 shadow-md"
    >
      <h2 className="text-xl font-semibold text-gray-900">Edit vacancy</h2>
      <div>
        <label htmlFor="vacancy-title" className={labelClass}>
          Job title *
        </label>
        <input
          id="vacancy-title"
          className={fieldClass}
          value={values.jobTitle}
          onChange={set('jobTitle')}
        />
      </div>
      <div>
        <label htmlFor="vacancy-description" className={labelClass}>
          Description
        </label>
        <textarea
          id="vacancy-description"
          rows={8}
          className={fieldClass}
          value={values.description}
          onChange={set('description')}
        />
      </div>
      <div>
        <label htmlFor="vacancy-location" className={labelClass}>
          Location
        </label>
        <input
          id="vacancy-location"
          className={fieldClass}
          value={values.jobLocation}
          onChange={set('jobLocation')}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div>
          <label htmlFor="vacancy-min-salary" className={labelClass}>
            Minimum salary
          </label>
          <input
            id="vacancy-min-salary"
            inputMode="numeric"
            className={fieldClass}
            value={values.minimumSalary}
            onChange={set('minimumSalary')}
          />
        </div>
        <div>
          <label htmlFor="vacancy-max-salary" className={labelClass}>
            Maximum salary
          </label>
          <input
            id="vacancy-max-salary"
            inputMode="numeric"
            className={fieldClass}
            value={values.maximumSalary}
            onChange={set('maximumSalary')}
          />
        </div>
        <div>
          <label htmlFor="vacancy-currency" className={labelClass}>
            Currency
          </label>
          <select
            id="vacancy-currency"
            className={fieldClass}
            value={values.currency}
            onChange={set('currency')}
          >
            {Array.from(new Set([values.currency, ...CURRENCIES])).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="vacancy-salary-time" className={labelClass}>
            Paid
          </label>
          <select
            id="vacancy-salary-time"
            className={fieldClass}
            value={values.salaryTime}
            onChange={set('salaryTime')}
          >
            {Array.from(new Set([values.salaryTime, ...SALARY_TIMES])).map(
              (t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ),
            )}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="vacancy-status" className={labelClass}>
            Status
          </label>
          <select
            id="vacancy-status"
            className={fieldClass}
            value={values.status}
            onChange={set('status')}
          >
            <option value="open">Open: accepting applications</option>
            <option value="closed">Closed: no new applications</option>
          </select>
        </div>
        <div>
          <label htmlFor="vacancy-visibility" className={labelClass}>
            Visibility
          </label>
          <select
            id="vacancy-visibility"
            className={fieldClass}
            value={values.visibility}
            onChange={set('visibility')}
          >
            <option value="public">Public: listed on your job portal</option>
            <option value="private">Private: internal only</option>
          </select>
        </div>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          className={secondaryButtonClass}
          onClick={onDone}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button type="submit" className={primaryButtonClass} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save vacancy'}
        </button>
      </div>
    </form>
  );
};

export default EditVacancy;
